// src/features/chat/EntityChatPanel.tsx
// Full chat surface embeddable on any entity's detail page or in the right
// rail. The conversation is parented to the entity: ListConversations
// (parent=entity) finds it, CreateConversation(parent=entity) starts it. Live
// updates come from wsClient when WS_CHAT_ENABLED; otherwise the panel works
// REST-only with a manual refresh button.
//
// Orchestrator only — the stream, composer, thread slide-over, attachments
// views and lightbox live in ./panel/.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert, Button } from '@react/components';

import { authStore } from '@core/stores';
import { attachmentApi, userprofileApi } from '@core/api';
import { ListConversations } from '@core/api/endpoints/chat/list_conversations';
import { CreateConversation } from '@core/api/endpoints/chat/create_conversation';
import { ListMessages } from '@core/api/endpoints/chat/list_messages';
import { SendMessage } from '@core/api/endpoints/chat/send_message';
import { GetUnreadCounts } from '@core/api/endpoints/chat/get_unread_counts';
import { ListConversationMembers } from '@core/api/endpoints/chat/list_conversation_members';
import { AddReaction } from '@core/api/endpoints/chat/add_reaction';
import { RemoveReaction } from '@core/api/endpoints/chat/remove_reaction';
import { PinMessage } from '@core/api/endpoints/chat/pin_message';
import { UnpinMessage } from '@core/api/endpoints/chat/unpin_message';
import { ListPinnedMessages } from '@core/api/endpoints/chat/list_pinned_messages';
import { EditMessage } from '@core/api/endpoints/chat/edit_message';
import { DeleteMessage } from '@core/api/endpoints/chat/delete_message';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { errMsg } from '@/features/task/panelHelpers';
import { getAttachmentDownloadUrl, isImageAttachment } from '@/features/chat/logic';
import type { AttachmentPreview } from '@core/types/chat';
import type { Entity } from '@core/types/entity';
import { CreateTaskFromMessage } from './CreateTaskFromMessage';
import { wsClient, WS_CHAT_ENABLED } from './wsClient';
import { PanelComposer } from './panel/PanelComposer';
import {
  mergePanelMessageAction,
  PanelMessageList,
  runPanelMessageAction,
  type PanelMessageAction,
  type PanelMessageActionFor,
} from './panel/PanelMessageList';
import { PanelThreadView } from './panel/PanelThreadView';
import { PanelAttachmentsView, type PanelView } from './panel/PanelAttachmentsView';
import { ChatLightbox, type LightboxImage } from './panel/ChatLightbox';
import { ScheduledMessagesBar } from './ScheduleSendControls';
import { ghostIconBtn, messageId, byOldest } from './panel/panelShared';
import { resolveComposerMetadata } from './entityChatPanelUtils';

interface EntityRef {
  id: string;
  type_name: string;
}

export interface EntityChatPanelProps {
  entity: EntityRef;
  title?: string;
  defaultConversationName?: string;
  onCreateTaskFromMessage?: (message: any) => void;
  composerMetadata?: Record<string, string> | null;
  composerContentRequired?: boolean;
  messageFilter?: (message: any) => boolean;
  renderAnchor?: (message: any) => ReactNode;
  onAnchor?: (message: any) => void;
  messageActionFor?: PanelMessageActionFor;
  messageActionError?: string;
}

const PAGE_SIZE = 50;

const VIEW_TABS: { value: PanelView; labelKey: string }[] = [
  { value: 'stream', labelKey: 'chat.entityPanel.viewStream' },
  { value: 'media', labelKey: 'chat.entityPanel.viewMedia' },
  { value: 'files', labelKey: 'chat.entityPanel.viewFiles' },
  { value: 'links', labelKey: 'chat.entityPanel.viewLinks' },
];

export const EntityChatPanel = observer(({
  entity,
  title,
  defaultConversationName,
  onCreateTaskFromMessage,
  composerMetadata,
  composerContentRequired = false,
  messageFilter,
  renderAnchor,
  onAnchor,
  messageActionFor,
  messageActionError,
}: EntityChatPanelProps) => {
  const { t, locale } = useI18n();

  const [conversation, setConversation] = useState<any | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [hasMoreBefore, setHasMoreBefore] = useState(false);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState<any | null>(null);
  const [taskFromMessage, setTaskFromMessage] = useState<any | null>(null);
  const [typingNames, setTypingNames] = useState<string[]>([]);
  const [memberNames, setMemberNames] = useState<Map<string, string>>(new Map());
  const [threadParent, setThreadParent] = useState<any | null>(null);
  const [messageActionLoadingId, setMessageActionLoadingId] = useState<string | null>(null);
  const [view, setView] = useState<PanelView>('stream');
  const [viewSwitcherOpen, setViewSwitcherOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // Pinned message ids — drives the Pin/Unpin toggle in each row's more menu.
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  // Composer edit mode: when set, the composer edits instead of sending.
  const [editingMessage, setEditingMessage] = useState<any | null>(null);
  // Delete confirmation — same ConfirmDialog idiom as ChatDetailPage.
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Poke for the scheduled bar after the composer schedules a message.
  const [scheduledRefreshKey, setScheduledRefreshKey] = useState(0);

  const conversationId: string = conversation?.entity?.id ?? conversation?.id ?? '';
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimers = useRef<Map<string, number>>(new Map());
  const downloadUrlCache = useRef<Map<string, string>>(new Map());
  const messageActionPending = useRef(false);

  const parent = { id: entity.id, type_name: entity.type_name } as Entity;

  // Host pages can take over task creation; without the prop the panel opens
  // its own dialog so the rail-mounted panel gets it for free.
  const handleCreateTaskFromMessage = (msg: any) => {
    if (onCreateTaskFromMessage) onCreateTaskFromMessage(msg);
    else setTaskFromMessage(msg);
  };

  // --- Attachment URLs -------------------------------------------------------

  const resolveAttachmentUrl = useCallback(async (attachmentId: string): Promise<string> => {
    const cached = downloadUrlCache.current.get(attachmentId);
    if (cached) return cached;
    const url = await getAttachmentDownloadUrl(attachmentId);
    downloadUrlCache.current.set(attachmentId, url);
    return url;
  }, []);

  const handleDownload = useCallback(async (attachmentId: string, fileName: string) => {
    try {
      const url = await resolveAttachmentUrl(attachmentId);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
    } catch {
      // silent
    }
  }, [resolveAttachmentUrl]);

  // --- Loading -------------------------------------------------------------

  const loadMessages = useCallback(async (convId: string) => {
    const res = await ListMessages(convId, {
      pagination: { limit: PAGE_SIZE, cursor: '', direction: 'backward' },
    } as any);
    const list: any = (res as any)?.messages;
    const items: any[] = Array.isArray(list) ? list : (list?.messages ?? []);
    setMessages([...items].sort(byOldest));
    setHasMoreBefore(Boolean((res as any)?.has_more_before));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ListConversations({
        parent,
        pagination: { pagination: { cursor: '', limit: 20, direction: 'forward' } },
      });
      const raw: any = res?.conversations;
      const list: any[] = Array.isArray(raw) ? raw : (raw?.conversations ?? []);
      const conv = list[0] ?? null;
      setConversation(conv);
      const convId = conv?.entity?.id ?? conv?.id;
      if (convId) {
        await loadMessages(convId);
        // Sender names come from member profiles — message payloads don't
        // carry display_name. Same enrichment as ChatDetailPage's profileMap.
        // Best-effort, never blocks the stream.
        try {
          const mres: any = await ListConversationMembers(convId, { pagination: { limit: 100, cursor: '', direction: 'forward' } } as any);
          const mraw: any = mres?.members;
          const mlist: any[] = Array.isArray(mraw) ? mraw : (mraw?.members ?? []);
          const names = new Map<string, string>(
            mlist
              .map((m: any) => [m?.profile?.entity?.id ?? '', (m?.profile?.display_name ?? '').trim()] as [string, string])
              .filter(([pid, name]) => pid && name),
          );
          const missingIds = mlist
            .map((m: any) => m?.profile?.entity?.id ?? '')
            .filter((pid: string) => pid && !names.get(pid));
          if (missingIds.length > 0) {
            try {
              const pres: any = await userprofileApi.ListUserprofilesByIDs({ ids: missingIds });
              const profiles = pres?.userprofiles?.profiles ?? [];
              for (const p of profiles) {
                if (!p?.entity?.id) continue;
                const name = p.display_name?.trim()
                  || [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
                  || p.username?.trim()
                  || '';
                if (name) names.set(p.entity.id, name);
              }
            } catch {
              // keep member-list names only
            }
          }
          setMemberNames(names);
        } catch {
          // names fall back to sender payload / "unknown"
        }
        // Unread badge — best-effort, never blocks the stream.
        try {
          const counts = await GetUnreadCounts({} as any);
          const entry = (counts as any)?.counts?.find(
            (c: any) => (c?.conversation?.id ?? '') === convId,
          );
          setUnread(entry?.unread_count ?? 0);
        } catch {
          // badge stays hidden
        }
      } else {
        setMessages([]);
      }
    } catch (e) {
      setError(errMsg(e, t('chat.entityPanel.loadError')));
      setConversation(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.id, entity.type_name, loadMessages]);

  useEffect(() => {
    if (entity.id) void load();
  }, [entity.id, load]);

  // Lazy-hydrate attachment previews. The chat service stores attachments as
  // entity refs only and does NOT populate attachment_previews on
  // SendMessage/ListMessages — resolve file_name/mime/size per entity id and
  // splice a synthesized preview list into local state. Same approach as
  // ChatDetailPage; a future batch RPC would replace the N-call loop.
  const hydratedAttachments = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (messages.length === 0) return;
    const pending: Array<{ msgId: string; entityIds: string[] }> = [];
    for (const m of messages) {
      const mid = messageId(m);
      if (!mid || hydratedAttachments.current.has(mid)) continue;
      const ents = m?.attachments?.entities ?? [];
      if (ents.length === 0) continue;
      const previews: AttachmentPreview[] = m?.attachment_previews?.items ?? [];
      if (previews.length >= ents.length) {
        hydratedAttachments.current.add(mid);
        continue;
      }
      const ids = ents.map((e: any) => e?.id).filter(Boolean) as string[];
      if (ids.length > 0) pending.push({ msgId: mid, entityIds: ids });
    }
    if (pending.length === 0) return;
    for (const p of pending) hydratedAttachments.current.add(p.msgId);

    let cancelled = false;
    (async () => {
      for (const { msgId, entityIds } of pending) {
        const previews: AttachmentPreview[] = [];
        for (const aid of entityIds) {
          try {
            const res: any = await attachmentApi.GetAttachment(aid);
            const att = res?.attachment;
            if (!att?.entity?.id) continue;
            previews.push({
              attachment: att.entity,
              file_name: att.file_name,
              mime_type: att.mime_type,
              size_bytes: att.size_bytes,
              type: att.attachment_type,
              thumbnail_url: att.media?.thumbnail_url,
            } as AttachmentPreview);
          } catch {
            // skip failed items; the rest still render
          }
        }
        if (cancelled || previews.length === 0) continue;
        setMessages((prev) => prev.map((m) => (
          messageId(m) === msgId ? { ...m, attachment_previews: { items: previews } } : m
        )));
      }
    })();
    return () => { cancelled = true; };
  }, [messages]);

  // Scroll to the newest message once the stream first renders.
  const didInitialScroll = useRef(false);
  useEffect(() => {
    if (!loading && messages.length > 0 && !didInitialScroll.current) {
      didInitialScroll.current = true;
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [loading, messages.length]);

  // --- Live updates ----------------------------------------------------------

  useEffect(() => {
    if (!WS_CHAT_ENABLED || !conversationId) return;
    const unsub = wsClient.subscribe('conv:' + conversationId, {
      onMessage: (msg: any) => {
        setMessages((prev) => {
          const id = messageId(msg);
          if (id && prev.some((m) => messageId(m) === id)) return prev;
          return [...prev, msg].sort(byOldest);
        });
      },
      onMessageEdited: (msg: any) => {
        const id = messageId(msg);
        setMessages((prev) => prev.map((m) => (messageId(m) === id ? { ...m, ...msg } : m)));
      },
      onMessageDeleted: (p: { message_id: string }) => {
        setMessages((prev) => prev.filter((m) => messageId(m) !== p.message_id));
      },
      onTyping: (p: any) => {
        const name: string = p?.sender?.display_name ?? p?.display_name ?? '';
        const key: string = p?.profile_id ?? p?.sender?.entity?.id ?? name;
        if (!key) return;
        setTypingNames((prev) => (prev.includes(name) ? prev : [...prev, name].filter(Boolean)));
        const timers = typingTimers.current;
        const existing = timers.get(key);
        if (existing) window.clearTimeout(existing);
        timers.set(key, window.setTimeout(() => {
          timers.delete(key);
          setTypingNames((prev) => prev.filter((n) => n !== name));
        }, 4000));
      },
    });
    return () => {
      unsub();
      for (const timer of typingTimers.current.values()) window.clearTimeout(timer);
      typingTimers.current.clear();
      setTypingNames([]);
    };
  }, [conversationId]);

  // --- Actions ---------------------------------------------------------------

  // Pinned ids, loaded once per conversation. Best-effort: when the fetch
  // fails the menu just shows "Pin message" for everything.
  useEffect(() => {
    if (!conversationId) {
      setPinnedIds(new Set());
      return;
    }
    let cancelled = false;
    ListPinnedMessages(conversationId, { pagination: { limit: 100, cursor: '', direction: 'forward' } } as any)
      .then((res: any) => {
        if (cancelled) return;
        const list: any[] = res?.pinned_messages?.pinned_messages ?? [];
        setPinnedIds(new Set(list.map((pm: any) => pm?.message?.id).filter(Boolean)));
      })
      .catch(() => { /* pinned state unknown — pin toggle falls back to "Pin" */ });
    return () => { cancelled = true; };
  }, [conversationId]);

  const handlePinMessage = async (msg: any) => {
    const msgId = messageId(msg);
    if (!msgId || !conversationId) return;
    setPinnedIds((prev) => new Set(prev).add(msgId));
    try {
      await PinMessage(conversationId, msgId, {} as any);
    } catch {
      setPinnedIds((prev) => {
        const next = new Set(prev);
        next.delete(msgId);
        return next;
      });
    }
  };

  const handleUnpinMessage = async (msg: any) => {
    const msgId = messageId(msg);
    if (!msgId || !conversationId) return;
    setPinnedIds((prev) => {
      const next = new Set(prev);
      next.delete(msgId);
      return next;
    });
    try {
      await UnpinMessage(conversationId, msgId);
    } catch {
      setPinnedIds((prev) => new Set(prev).add(msgId));
    }
  };

  const handleCopyMessageLink = async (msg: any) => {
    const msgId = messageId(msg);
    if (!msgId || !conversationId) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/chats/${conversationId}?m=${msgId}`);
    } catch {
      // silent — clipboard perms
    }
  };

  const handleStartEdit = (msg: any) => {
    setReplyTo(null);
    setEditingMessage(msg);
    setView('stream');
    textareaRef.current?.focus();
  };

  const handleSubmitEdit = async (content: string): Promise<boolean> => {
    const msgId = messageId(editingMessage);
    if (!msgId || !conversationId || !content) return false;
    try {
      const res: any = await EditMessage(conversationId, msgId, { content } as any);
      const updated = res?.message ?? res;
      if (updated && messageId(updated)) {
        setMessages((prev) => prev.map((m) => (messageId(m) === msgId ? { ...m, ...updated } : m)));
      } else {
        setMessages((prev) => prev.map((m) => (messageId(m) === msgId ? { ...m, content } : m)));
      }
      setEditingMessage(null);
      return true;
    } catch (e) {
      setError(errMsg(e, t('chat.entityPanel.sendError')));
      return false;
    }
  };

  const handleMessageAction = async (msg: any, action: PanelMessageAction) => {
    const msgId = messageId(msg);
    if (!msgId || !conversationId || messageActionPending.current) return;
    messageActionPending.current = true;
    setMessageActionLoadingId(msgId);
    setError(null);
    try {
      const res: any = await runPanelMessageAction(
        action,
        () => EditMessage(conversationId, msgId, action.request as any),
      );
      const updated = res?.message ?? res;
      const canonical = updated && messageId(updated) ? updated : undefined;
      const merge = (current: any) => mergePanelMessageAction(current, action, canonical);
      setMessages((prev) => prev.map((m) => (messageId(m) === msgId ? merge(m) : m)));
      setThreadParent((prev: any | null) => (prev && messageId(prev) === msgId ? merge(prev) : prev));
    } catch (e) {
      setError(errMsg(e, messageActionError || t('chat.entityPanel.sendError')));
    } finally {
      messageActionPending.current = false;
      setMessageActionLoadingId(null);
    }
  };

  const handleDeleteMessage = (msg: any) => {
    if (messageId(msg)) setDeleteTarget(msg);
  };

  const confirmDelete = async () => {
    const msgId = messageId(deleteTarget);
    if (!msgId || !conversationId) return;
    setIsDeleting(true);
    try {
      await DeleteMessage(conversationId, msgId);
      setMessages((prev) => prev.filter((m) => messageId(m) !== msgId));
      setDeleteTarget(null);
    } catch {
      // silent — the row stays, user can retry
    } finally {
      setIsDeleting(false);
    }
  };
  const startDiscussion = async () => {
    setCreating(true);
    setError(null);
    try {
      const res: any = await CreateConversation({
        type: 'CONVERSATION_TYPE_GROUP',
        name: defaultConversationName || title || t('chat.entityPanel.fallbackName'),
        parent,
      } as any);
      const conv = res?.conversation;
      if (conv) {
        setConversation(conv);
        setMessages([]);
        setHasMoreBefore(false);
      } else {
        await load();
      }
    } catch (e) {
      setError(errMsg(e, t('chat.entityPanel.createError')));
    } finally {
      setCreating(false);
    }
  };

  const loadEarlier = async () => {
    if (!conversationId || messages.length === 0) return;
    setLoadingEarlier(true);
    try {
      const oldest = messages[0];
      const res = await ListMessages(conversationId, {
        pagination: { limit: PAGE_SIZE, cursor: '', direction: 'backward' },
        before_id: messageId(oldest),
      } as any);
      const raw: any = (res as any)?.messages;
      const items: any[] = Array.isArray(raw) ? raw : (raw?.messages ?? []);
      const known = new Set(messages.map(messageId));
      setMessages((prev) => [...items.filter((m) => !known.has(messageId(m))), ...prev].sort(byOldest));
      setHasMoreBefore(Boolean((res as any)?.has_more_before));
    } catch (e) {
      setError(errMsg(e, t('chat.entityPanel.loadError')));
    } finally {
      setLoadingEarlier(false);
    }
  };

  const refresh = async () => {
    if (!conversationId) return void load();
    setError(null);
    try {
      await loadMessages(conversationId);
    } catch (e) {
      setError(errMsg(e, t('chat.entityPanel.loadError')));
    }
  };

  const send = async (content: string, attachmentIds: string[], scheduledAt?: Date | null): Promise<boolean> => {
    if ((!content && attachmentIds.length === 0) || !conversationId || sending) return false;
    if ((composerContentRequired && !content) || (composerMetadata === null && !replyTo)) return false;
    const metadata = resolveComposerMetadata(composerMetadata, Boolean(replyTo));
    setSending(true);
    setError(null);
    try {
      const res: any = await SendMessage(conversationId, {
        content,
        content_type: 'MESSAGE_CONTENT_TYPE_TEXT',
        client_id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        ...(attachmentIds.length > 0
          ? { attachments: { entities: attachmentIds.map((id) => ({ id })) } }
          : {}),
        ...(replyTo ? { reply_to: { message_id: messageId(replyTo) } } : {}),
        ...(metadata ? { metadata } : {}),
        ...(scheduledAt ? { scheduled_at: scheduledAt.toISOString() } : {}),
      } as any);
      const msg = res?.message ?? res;
      if (scheduledAt) {
        // Scheduled: stored server-side, published when due — it belongs in
        // the scheduled bar, not the live stream.
        setScheduledRefreshKey((k) => k + 1);
      } else if (msg && messageId(msg)) {
        setMessages((prev) =>
          prev.some((m) => messageId(m) === messageId(msg)) ? prev : [...prev, msg].sort(byOldest),
        );
      }
      setReplyTo(null);
      const scroller = scrollRef.current;
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
      return true;
    } catch (e) {
      setError(errMsg(e, t('chat.entityPanel.sendError')));
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleReaction = async (msg: any, emoji: string) => {
    const msgId = messageId(msg);
    if (!msgId || !conversationId) return;
    const existing = (msg?.reactions?.items ?? []).find(
      (r: any) => r.emoji === emoji && r.me,
    );
    const adding = !existing;

    // Optimistic toggle; the WS broadcast overwrites with canonical state.
    setMessages((prev) => prev.map((m) => {
      if (messageId(m) !== msgId) return m;
      const items = [...(m?.reactions?.items ?? [])];
      const idx = items.findIndex((r: any) => r.emoji === emoji);
      if (adding) {
        if (idx >= 0) items[idx] = { ...items[idx], count: items[idx].count + 1, me: true };
        else items.push({ emoji, count: 1, reactor_ids: [], me: true });
      } else if (idx >= 0) {
        const next = { ...items[idx], count: items[idx].count - 1, me: false };
        if (next.count <= 0) items.splice(idx, 1);
        else items[idx] = next;
      }
      return { ...m, reactions: { items } };
    }));

    try {
      if (adding) await AddReaction(conversationId, msgId, { emoji } as any);
      else await RemoveReaction(conversationId, msgId, emoji);
    } catch {
      // silent — corrected on next reload
    }
  };

  const handleReply = (msg: any) => {
    setEditingMessage(null);
    setReplyTo(msg);
    setView('stream');
    textareaRef.current?.focus();
  };

  const jumpToMessage = (msgId: string) => {
    setView('stream');
    // After the stream renders again; no-op when the message isn't loaded.
    requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector(`[data-message-id="${msgId}"]`);
      el?.scrollIntoView({ block: 'center' });
    });
  };

  const visibleMessages = messageFilter ? messages.filter(messageFilter) : messages;

  // All image attachments of the loaded conversation, chronological — the
  // lightbox navigates across this whole set.
  const lightboxImages: LightboxImage[] = [];
  for (const m of visibleMessages) {
    for (const p of (m?.attachment_previews?.items ?? []) as AttachmentPreview[]) {
      if (isImageAttachment(p) && p.attachment?.id) {
        lightboxImages.push({ id: p.attachment.id, name: p.file_name });
      }
    }
  }

  const openImage = (attachmentId: string) => {
    const idx = lightboxImages.findIndex((img) => img.id === attachmentId);
    if (idx >= 0) setLightboxIndex(idx);
  };

  // --- Render ------------------------------------------------------------------

  const chatTitle = title || conversation?.name || t('chat.entityPanel.fallbackName');
  const myProfileId = authStore.activeProfileId;

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-5 w-1/3 animate-pulse rounded bg-muted" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-2">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-10 flex-1 animate-pulse rounded-md bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <p className="text-sm text-muted-foreground">{t('chat.entityPanel.empty')}</p>
        <div>
          <Button size="sm" variant="primary" isLoading={creating} onClick={startDiscussion}>
            {t('chat.entityPanel.start')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <h3 className="truncate text-sm font-semibold text-foreground">{chatTitle}</h3>
        {unread > 0 && (
          <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
            {unread}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setViewSwitcherOpen((v) => !v);
              if (view !== 'stream') setView('stream');
            }}
            className={ghostIconBtn}
            aria-label={t('chat.entityPanel.attachments')}
            title={t('chat.entityPanel.attachments')}
            aria-expanded={viewSwitcherOpen}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l8.57-8.57A4 4 0 1118 8.84l-8.59 8.57a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          {!WS_CHAT_ENABLED && (
            <button
              type="button"
              onClick={refresh}
              className={ghostIconBtn}
              aria-label={t('chat.entityPanel.refresh')}
              title={t('chat.entityPanel.refresh')}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12a9 9 0 11-2.64-6.36" />
                <path d="M21 3v6h-6" />
              </svg>
            </button>
          )}
          <Link
            to={`/chats/${conversationId}`}
            className={ghostIconBtn}
            aria-label={t('chat.entityPanel.openInChats')}
            title={t('chat.entityPanel.openInChats')}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
            </svg>
          </Link>
        </div>
      </div>

      {/* View switcher */}
      {viewSwitcherOpen && (
        <div className="flex gap-0.5 rounded-lg bg-muted/50 p-0.5" role="tablist">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={view === tab.value}
              onClick={() => setView(tab.value)}
              className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${
                view === tab.value
                  ? 'bg-accent font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
      )}

      {error && (
        <Alert variant="error">
          <div className="flex items-center gap-2">
            <span className="flex-1">{error}</span>
            <button
              type="button"
              onClick={refresh}
              className="shrink-0 rounded px-2 py-0.5 text-xs underline transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('chat.entityPanel.retry')}
            </button>
          </div>
        </Alert>
      )}

      {view === 'stream' ? (
        <>
          {/* Stream */}
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto pr-1">
            {hasMoreBefore && (
              <div className="pb-2 text-center">
                <button
                  type="button"
                  onClick={loadEarlier}
                  disabled={loadingEarlier}
                  className="rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  {loadingEarlier ? '…' : t('chat.entityPanel.loadEarlier')}
                </button>
              </div>
            )}
            {visibleMessages.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('chat.entityPanel.emptyConversation')}
              </p>
            ) : (
              <PanelMessageList
                messages={visibleMessages}
                memberNames={memberNames}
                myProfileId={myProfileId}
                locale={locale}
                pinnedIds={pinnedIds}
                resolveAttachmentUrl={resolveAttachmentUrl}
                onReply={handleReply}
                onOpenThread={setThreadParent}
                onCreateTask={handleCreateTaskFromMessage}
                onReaction={(msg, emoji) => void handleReaction(msg, emoji)}
                onOpenImage={openImage}
                onDownload={(aid, name) => void handleDownload(aid, name)}
                onCopyLink={(msg) => void handleCopyMessageLink(msg)}
                onPin={(msg) => void handlePinMessage(msg)}
                onUnpin={(msg) => void handleUnpinMessage(msg)}
                onEdit={handleStartEdit}
                onDelete={handleDeleteMessage}
                renderAnchor={renderAnchor}
                onAnchor={onAnchor}
                messageActionFor={messageActionFor}
                messageActionLoadingId={messageActionLoadingId}
                onMessageAction={(msg, action) => void handleMessageAction(msg, action)}
              />
            )}
          </div>

          {/* Typing indicator */}
          {typingNames.length > 0 && (
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {t('chat.entityPanel.typing', { name: typingNames.join(', ') })}
            </p>
          )}

          {/* Editing banner — composer switches to edit mode */}
          {editingMessage && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                {t('common.edit')}: {editingMessage?.content ?? ''}
              </span>
              <button
                type="button"
                onClick={() => setEditingMessage(null)}
                className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('common.cancel')}
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Reply banner */}
          {replyTo && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                {t('chat.entityPanel.replyPrefix')}{' '}
                {replyTo?.sender?.display_name ?? ''}: {replyTo?.content ?? ''}
              </span>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('chat.entityPanel.cancelReply')}
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {conversationId && (
            <ScheduledMessagesBar
              conversationId={conversationId}
              refreshKey={scheduledRefreshKey}
              locale={locale}
            />
          )}

          {(composerMetadata !== null || replyTo || editingMessage) && (
            <PanelComposer
              sending={sending}
              onSend={send}
              textareaRef={textareaRef}
              editing={editingMessage ? { id: messageId(editingMessage), content: editingMessage?.content ?? '' } : null}
              onSubmitEdit={handleSubmitEdit}
            />
          )}
        </>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <PanelAttachmentsView
            view={view}
            messages={visibleMessages}
            resolveUrl={resolveAttachmentUrl}
            onOpenImage={openImage}
            onDownload={(aid, name) => void handleDownload(aid, name)}
            onJump={jumpToMessage}
          />
        </div>
      )}

      {/* Thread slide-over */}
      {threadParent && (
        <PanelThreadView
          conversationId={conversationId}
          parentMessage={threadParent}
          memberNames={memberNames}
          myProfileId={myProfileId}
          locale={locale}
          onClose={() => setThreadParent(null)}
          resolveAttachmentUrl={resolveAttachmentUrl}
          onOpenImage={openImage}
          onDownload={(aid, name) => void handleDownload(aid, name)}
          renderAnchor={renderAnchor}
          onAnchor={onAnchor}
          messageActionFor={messageActionFor}
          messageActionLoadingId={messageActionLoadingId}
          onMessageAction={(msg, action) => void handleMessageAction(msg, action)}
        />
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ChatLightbox
          images={lightboxImages}
          index={lightboxIndex}
          resolveUrl={resolveAttachmentUrl}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('chat.deleteMessageConfirm')}
        destructive
        confirmLabel={t('common.delete')}
        isConfirming={isDeleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => { if (!isDeleting) setDeleteTarget(null); }}
      />

      <CreateTaskFromMessage
        open={taskFromMessage !== null}
        onOpenChange={(v) => { if (!v) setTaskFromMessage(null); }}
        message={taskFromMessage}
        conversationId={conversationId}
        parentEntity={parent}
      />
    </div>
  );
});

export default EntityChatPanel;
